import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  UserGroupIcon,
  UserIcon,
  ChartBarIcon,
  BookOpenIcon,
  AcademicCapIcon,
  ClipboardDocumentListIcon,
  BuildingOffice2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    return savedState === null ? true : savedState === 'true';
  });
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeLink, setActiveLink] = useState('');

  // 현재 경로에 맞게 활성 링크 설정
  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location]);

  // 컴포넌트 마운트 시 초기 상태를 localStorage에 저장
  useEffect(() => {
    if (localStorage.getItem('sidebarCollapsed') === null) {
      localStorage.setItem('sidebarCollapsed', 'true');
    }
  }, []);

  const handleLogout = () => {
    const success = logout();
    if (success) {
      console.log('로그아웃 처리 완료');
    } else {
      console.error('로그아웃 처리 중 문제가 발생했습니다');
    }
    // 로그인 페이지로 리다이렉트
    navigate('/login');
  };

  // 사이드바 접기/펼치기 토글
  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState.toString());
  };

  // 사용자 권한에 따른 메뉴 항목 필터링
  const getNavItems = () => {
    // 기본 메뉴 항목 정의
    const items = [
      { name: '대시보드', href: '/dashboard', icon: HomeIcon, roles: ['student', 'instructor', 'admin'] },
      { name: '강의 출석 관리', href: '/attendance', icon: ClipboardDocumentListIcon, roles: ['instructor', 'admin'] },
      { name: '교육생 관리', href: '/students', icon: UserGroupIcon, roles: ['instructor', 'admin'] },
      { name: '통합 강의 관리', href: '/integrated-class-management', icon: BookOpenIcon, roles: ['admin', 'instructor'] },
      { name: '강의실 관리', href: '/classrooms', icon: BuildingOffice2Icon, roles: ['admin', 'instructor'] },
      // { name: '강의 구성 관리', href: '/course-structure', icon: BookOpenIcon, roles: ['admin'] },
      { name: '회원 관리', href: '/users', icon: UserIcon, roles: ['admin'] },
      { name: '보고서', href: '/reports', icon: ChartBarIcon, roles: ['admin'] },
    ];

    console.log('사용자 정보 (메뉴 렌더링):', user);
    
    // 사용자 정보가 없거나 역할 정보가 없는 경우 대시보드만 표시
    if (!user || !user.roles || !Array.isArray(user.roles)) {
      console.warn('사용자 역할 정보가 없습니다.');
      return items.filter(item => item.href === '/dashboard');
    }
    
    // 강사인 경우 출결관리 메뉴만 보이게 설정
    if (user.roles.includes('instructor') && !user.roles.includes('admin')) {
      return items.filter(item => ['dashboard', 'attendance', 'students'].includes(item.href.substring(1)));
    }
    
    // 사용자 역할에 따라 필터링
    const filteredItems = items.filter(item => 
      item.roles.some(role => user.roles.includes(role))
    );
    
    return filteredItems;
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* 모바일용 사이드바 */}
      <div className={`md:hidden fixed inset-0 z-40 ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-75" 
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        ></div>
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-indigo-700 transition-all duration-300 ease-in-out transform">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">사이드바 닫기</span>
              <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
            </button>
          </div>
          <div className="h-full px-3 py-4 overflow-y-auto">
            <div className="flex items-center justify-center mb-5 pt-4 pb-6">
              <Link to="/dashboard" className="flex items-center" onClick={() => setSidebarOpen(false)}>
                <span className="self-center text-xl font-semibold whitespace-nowrap text-white">
                  출결관리시스템
                </span>
              </Link>
            </div>
            <ul className="space-y-2 font-medium">
              {getNavItems().map((item) => (
                <li key={item.name} className={`nav-item ${activeLink === item.href ? 'bg-indigo-800' : ''}`}>
                  <Link
                    to={item.href}
                    className="flex items-center p-2 text-base font-medium text-white rounded-lg hover:bg-indigo-800 group"
                    onClick={() => {
                      setActiveLink(item.href);
                      setSidebarOpen(false);
                    }}
                  >
                    <item.icon className="w-6 h-6 text-white" aria-hidden="true" />
                    <span className="ml-3">{item.name}</span>
                  </Link>
                </li>
              ))}
              <li className="pt-4 mt-4 border-t border-indigo-800">
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full p-2 text-base font-medium text-white rounded-lg hover:bg-indigo-800 group"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="ml-3">로그아웃</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* 데스크톱용 사이드바 */}
      <div className={`hidden md:flex md:flex-shrink-0 transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'md:w-16' : 'md:w-64'}`}>
        <div className={`flex flex-col ${sidebarCollapsed ? 'w-16' : 'w-64'} transition-all duration-300`}>
          <div className="flex-1 flex flex-col min-h-0 bg-indigo-700">
            <div className="h-full px-3 py-4 overflow-y-auto relative">
              <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} mb-5 pt-2`}>
                {!sidebarCollapsed && (
                  <Link to="/dashboard" className="flex items-center">
                    <span className="self-center text-xl font-semibold whitespace-nowrap text-white">
                      출결관리시스템
                    </span>
                  </Link>
                )}
                <button
                  onClick={toggleSidebar}
                  className="p-1 text-white rounded-full hover:bg-indigo-800 focus:outline-none"
                >
                  {sidebarCollapsed ? (
                    <ChevronRightIcon className="h-5 w-5" />
                  ) : (
                    <ChevronLeftIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              <ul className="space-y-2 font-medium">
                {getNavItems().map((item) => (
                  <li key={item.name} className={`nav-item ${activeLink === item.href ? 'bg-indigo-800' : ''}`}>
                    <Link
                      to={item.href}
                      className={`flex items-center p-2 text-base font-medium text-white rounded-lg hover:bg-indigo-800 group ${
                        sidebarCollapsed ? 'justify-center' : ''
                      }`}
                      onClick={() => setActiveLink(item.href)}
                      title={sidebarCollapsed ? item.name : ''}
                    >
                      <item.icon className="w-6 h-6 text-white" aria-hidden="true" />
                      {!sidebarCollapsed && <span className="ml-3">{item.name}</span>}
                    </Link>
                  </li>
                ))}
                <li className={`pt-4 mt-4 border-t border-indigo-800 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
                  <button
                    onClick={handleLogout}
                    className={`flex items-center ${
                      sidebarCollapsed ? 'justify-center p-2 w-10' : 'w-full p-2'
                    } text-base font-medium text-white rounded-lg hover:bg-indigo-800 group`}
                    title={sidebarCollapsed ? '로그아웃' : ''}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    {!sidebarCollapsed && <span className="ml-3">로그아웃</span>}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 메인 컨텐츠 */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <div className="md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-white shadow-sm">
          <button
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">사이드바 열기</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            <span className="ml-2 text-sm font-medium hidden sm:inline-block">메뉴</span>
          </button>
          <span className="ml-2 sm:ml-4 text-lg font-semibold text-indigo-700 inline-block">
            {getNavItems().find(item => item.href === activeLink)?.name || '출결관리시스템'}
          </span>
        </div>
        <main className="flex-1 relative z-0 overflow-y-auto bg-gray-50 focus:outline-none p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout; 